import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '@material-ui/core';
import { Subject as SubjectIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateHazardCause } from '../Hazard.hooks';
import { BaseHazardCausesCardProps, HazardCause } from '../Hazard.interface';

const HazardCauseInfoCard: React.FC<BaseHazardCausesCardProps> = (
  props: BaseHazardCausesCardProps
) => {
  const {
    title = 'Details',
    hazardId,
    hazardCause,
    hazardCauseVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = hazardCauseVersion || hazardCause;
  const initialValues: Partial<HazardCause> = {
    title: initialData.title || '',
    description: initialData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/hazards/${hazardId}/causes/${hazardCause.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedHazardCause,
    isUpdatingHazardCause: isUpdating,
    updateHazardCauseError: updateError,
    updateHazardCause
  } = useUpdateHazardCause(hazardId, hazardCause.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(hazardCause.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(hazardCause.task);

  // notify parent when a user need info is updated
  useEffect(() => {
    if (updatedHazardCause) {
      onChange?.(updatedHazardCause);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedHazardCause]);

  useEffect(() => {
    if (updatedTask) {
      hazardCause.task = Object.assign(hazardCause.task || {}, updatedTask);
      onChange?.(hazardCause);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <InlineFormik
      initialValues={initialValues}
      isSubmitting={isUpdating}
      error={updateError}
      onSubmit={(values: Partial<HazardCause>) => {
        updateHazardCause(values);
        onChange?.(values);
      }}
    >
      <Card style={hidden ? { display: 'none' } : undefined}>
        <CardTitle>
          <SubjectIcon />
          {enableTitleRedirect ? (
            <Link className='link' to={getPath()}>
              {title}
            </Link>
          ) : (
            title
          )}
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            <InlineTextField
              name='title'
              label='Title'
              placeholder='Enter title for Hazard Cause'
              disabled={
                Boolean(hazardCauseVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            <InlineHtml
              name='description'
              label='Description'
              helperText='Provide an overview of the user need.'
              disabled={
                Boolean(hazardCauseVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={
                hazardCauseVersion?.attachments ||
                hazardCause.task.attachments ||
                []
              }
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (hazardCause.task) {
                  hazardCause.task.attachments = attachments;
                }
                onChange?.(hazardCause);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};

export default HazardCauseInfoCard;
